import {
  MAIL_STORAGE_INBOX_MAX,
  MAIL_STORAGE_MAX_BYTES,
} from "@/lib/constants/mailStorage";

const DB_NAME = "aurelia_mail_storage_v1";
const DB_VERSION = 1;
const STORE = "eml";

export type MailEmlStoreRecord = {
  id: string;
  accountId: number;
  folder: string;
  uid: number;
  byteSize: number;
  savedAt: number;
  /** Timestamp ms pour FIFO INBOX. */
  messageDateMs: number;
  eml: ArrayBuffer;
};

export type MailStorageStats = {
  usedBytes: number;
  maxBytes: number;
  usedPercent: number;
  messageCount: number;
  inboxCountByAccount: Record<number, number>;
};

export type MailStorageFolderSummary = {
  folder: string;
  messageCount: number;
  usedBytes: number;
};

export type MailStoragePurgeResult = {
  removed: number;
  freedBytes: number;
  removedEntries: Array<{ accountId: number; folder: string; uid: number }>;
};

export type AccountMailStorageStats = {
  usedBytes: number;
  maxBytes: number;
  usedPercent: number;
  messageCount: number;
  inboxCount: number;
};

let dbPromise: Promise<IDBDatabase> | null = null;

export function emlStoreId(accountId: number, folder: string, uid: number): string {
  return `${accountId}\0${folder}\0${uid}`;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible."));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("accountId", "accountId", { unique: false });
        store.createIndex("savedAt", "savedAt", { unique: false });
        store.createIndex("messageDateMs", "messageDateMs", { unique: false });
        store.createIndex("accountFolder", ["accountId", "folder"], { unique: false });
      }
    };
  });

  return dbPromise;
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
  return openDb().then(
    (db) =>
      new Promise<T | void>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = fn(store);

        tx.oncomplete = () => {
          if (request && "result" in request) {
            resolve((request as IDBRequest<T>).result);
          } else {
            resolve();
          }
        };
        tx.onerror = () => reject(tx.error ?? new Error("Transaction IndexedDB échouée."));
        if (request) {
          request.onerror = () => reject(request.error ?? new Error("Requête IndexedDB échouée."));
        }
      })
  );
}

function parseMessageDateMs(dateRaw: string | undefined): number {
  if (!dateRaw?.trim()) return Date.now();
  const parsed = Date.parse(dateRaw);
  return Number.isFinite(parsed) ? parsed : Date.now();
}

export async function idbHasEml(
  accountId: number,
  folder: string,
  uid: number
): Promise<boolean> {
  if (uid <= 0 || accountId <= 0) return false;
  const record = (await runTransaction<MailEmlStoreRecord | undefined>("readonly", (store) =>
    store.get(emlStoreId(accountId, folder, uid))
  )) as MailEmlStoreRecord | undefined;
  return Boolean(record?.eml);
}

export async function idbGetStoredUids(
  accountId: number,
  folder: string
): Promise<Set<number>> {
  if (accountId <= 0) return new Set();

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const index = store.index("accountFolder");
    const req = index.openCursor(IDBKeyRange.only([accountId, folder]));
    const uids = new Set<number>();

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(uids);
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      if (record.uid > 0) uids.add(record.uid);
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

async function getTotalUsedBytes(excludeId?: string): Promise<number> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.openCursor();
    let total = 0;

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(total);
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      if (!excludeId || record.id !== excludeId) {
        total += record.byteSize ?? 0;
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

async function enforceInboxFifo(accountId: number, folder: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("accountFolder");
    const req = index.openCursor(IDBKeyRange.only([accountId, folder]));
    const records: MailEmlStoreRecord[] = [];

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        if (records.length <= MAIL_STORAGE_INBOX_MAX) {
          resolve();
          return;
        }
        records.sort((a, b) => a.messageDateMs - b.messageDateMs);
        const toRemove = records.length - MAIL_STORAGE_INBOX_MAX;
        let removed = 0;
        for (let i = 0; i < toRemove; i += 1) {
          store.delete(records[i].id);
          removed += 1;
        }
        if (removed >= toRemove) resolve();
        return;
      }
      records.push(cursor.value as MailEmlStoreRecord);
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbPutEml(params: {
  accountId: number;
  folder: string;
  uid: number;
  eml: ArrayBuffer;
  messageDate?: string;
}): Promise<boolean> {
  const { accountId, folder, uid, eml, messageDate } = params;
  if (uid <= 0 || accountId <= 0 || eml.byteLength === 0) return false;

  const id = emlStoreId(accountId, folder, uid);
  const byteSize = eml.byteLength;
  const existingBytes = await getTotalUsedBytes(id);
  if (existingBytes + byteSize > MAIL_STORAGE_MAX_BYTES) {
    return false;
  }

  const record: MailEmlStoreRecord = {
    id,
    accountId,
    folder,
    uid,
    byteSize,
    savedAt: Date.now(),
    messageDateMs: parseMessageDateMs(messageDate),
    eml,
  };

  await runTransaction("readwrite", (store) => store.put(record));
  if (folder.toUpperCase() === "INBOX") {
    await enforceInboxFifo(accountId, folder);
  }
  return true;
}

export async function idbGetEmlBlob(
  accountId: number,
  folder: string,
  uid: number
): Promise<ArrayBuffer | null> {
  if (uid <= 0 || accountId <= 0) return null;
  const record = (await runTransaction<MailEmlStoreRecord | undefined>("readonly", (store) =>
    store.get(emlStoreId(accountId, folder, uid))
  )) as MailEmlStoreRecord | undefined;
  return record?.eml ?? null;
}

export async function idbRelocateEml(
  accountId: number,
  uid: number,
  fromFolder: string,
  toFolder: string
): Promise<void> {
  if (uid <= 0 || accountId <= 0 || fromFolder === toFolder) return;

  const oldId = emlStoreId(accountId, fromFolder, uid);
  const existing = (await runTransaction<MailEmlStoreRecord | undefined>("readonly", (store) =>
    store.get(oldId)
  )) as MailEmlStoreRecord | undefined;

  if (!existing?.eml) return;

  const newRecord: MailEmlStoreRecord = {
    ...existing,
    id: emlStoreId(accountId, toFolder, uid),
    folder: toFolder,
    savedAt: Date.now(),
  };

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    store.delete(oldId);
    store.put(newRecord);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGetMailStorageStats(): Promise<MailStorageStats> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.openCursor();
    let usedBytes = 0;
    let messageCount = 0;
    const inboxCountByAccount: Record<number, number> = {};

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        const usedPercent =
          MAIL_STORAGE_MAX_BYTES > 0
            ? Math.min(100, (usedBytes / MAIL_STORAGE_MAX_BYTES) * 100)
            : 0;
        resolve({
          usedBytes,
          maxBytes: MAIL_STORAGE_MAX_BYTES,
          usedPercent,
          messageCount,
          inboxCountByAccount,
        });
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      usedBytes += record.byteSize ?? 0;
      messageCount += 1;
      if (record.folder.toUpperCase() === "INBOX") {
        inboxCountByAccount[record.accountId] =
          (inboxCountByAccount[record.accountId] ?? 0) + 1;
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGetAccountMailStorageStats(
  accountId: number
): Promise<AccountMailStorageStats> {
  if (accountId <= 0) {
    return {
      usedBytes: 0,
      maxBytes: MAIL_STORAGE_MAX_BYTES,
      usedPercent: 0,
      messageCount: 0,
      inboxCount: 0,
    };
  }

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));
    let usedBytes = 0;
    let messageCount = 0;
    let inboxCount = 0;

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        const usedPercent =
          MAIL_STORAGE_MAX_BYTES > 0
            ? Math.min(100, (usedBytes / MAIL_STORAGE_MAX_BYTES) * 100)
            : 0;
        resolve({
          usedBytes,
          maxBytes: MAIL_STORAGE_MAX_BYTES,
          usedPercent,
          messageCount,
          inboxCount,
        });
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      usedBytes += record.byteSize ?? 0;
      messageCount += 1;
      if (record.folder.toUpperCase() === "INBOX") inboxCount += 1;
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbListStoredFolders(
  accountId: number
): Promise<MailStorageFolderSummary[]> {
  if (accountId <= 0) return [];

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));
    const byFolder = new Map<string, MailStorageFolderSummary>();

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        resolve(
          [...byFolder.values()].sort((a, b) => a.folder.localeCompare(b.folder, "fr"))
        );
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      const prev = byFolder.get(record.folder) ?? {
        folder: record.folder,
        messageCount: 0,
        usedBytes: 0,
      };
      prev.messageCount += 1;
      prev.usedBytes += record.byteSize ?? 0;
      byFolder.set(record.folder, prev);
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbPurgeAccountStorage(params: {
  accountId: number;
  beforeDateMs?: number;
  folder?: string;
}): Promise<MailStoragePurgeResult> {
  const { accountId, beforeDateMs, folder } = params;
  if (accountId <= 0) {
    return { removed: 0, freedBytes: 0, removedEntries: [] };
  }

  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));
    const toRemove: MailEmlStoreRecord[] = [];

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        let freedBytes = 0;
        for (const record of toRemove) {
          store.delete(record.id);
          freedBytes += record.byteSize ?? 0;
        }
        resolve({
          removed: toRemove.length,
          freedBytes,
          removedEntries: toRemove.map((r) => ({
            accountId: r.accountId,
            folder: r.folder,
            uid: r.uid,
          })),
        });
        return;
      }
      const record = cursor.value as MailEmlStoreRecord;
      const matchFolder = folder == null || folder === "" || record.folder === folder;
      const matchDate =
        beforeDateMs == null ||
        !Number.isFinite(beforeDateMs) ||
        record.messageDateMs < beforeDateMs;
      if (matchFolder && matchDate) {
        toRemove.push(record);
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbClearMailStorageAccount(accountId: number): Promise<void> {
  if (accountId <= 0) return;

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbClearAllMailStorage(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const req = store.clear();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function decodeEmlBase64(emlBase64: string): ArrayBuffer {
  const binary = atob(emlBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
